import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArhamState } from './mascotTypes';
import { ArhamCharacter } from './Arham';

interface ArhamAnimationProps {
  state: ArhamState;
}

export const ArhamAnimation: React.FC<ArhamAnimationProps> = ({ state }) => {
  // Body motion variants for different states
  const bodyVariants: any = {
    idle: { y: [0, -4, 0], transition: { repeat: Infinity, duration: 2.5, ease: "easeInOut" } },
    wave: { y: [0, -6, 0], transition: { repeat: Infinity, duration: 1.8, ease: "easeInOut" } },
    point: { rotate: [0, -2, 2, 0], transition: { repeat: Infinity, duration: 3 } },
    celebrate: { y: [0, -16, 0], rotate: [0, 4, -4, 4, 0], transition: { repeat: Infinity, duration: 0.5 } },
    learn: { y: [0, -3, 0], transition: { repeat: Infinity, duration: 2.2, ease: "easeInOut" } },
    listen: { rotate: [0, 3, 0, 3], transition: { repeat: Infinity, duration: 2 } }
  };

  return (
    <div className="relative w-28 h-32 flex-shrink-0 flex items-center justify-center select-none">
      <motion.div
        animate={state}
        variants={bodyVariants}
        className="relative flex items-center justify-center w-full h-full"
      >
        <AnimatePresence mode="wait">
          {/* Party Hat / Celebration Crown */}
          {state === 'celebrate' && (
            <motion.div
              key="celebrate-accessory"
              initial={{ scale: 0, opacity: 0, y: 15 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0, opacity: 0, y: 10 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
              className="absolute -top-7 z-30 w-8 h-10 flex items-center justify-center"
            >
              <div className="w-0 h-0 border-l-[14px] border-l-transparent border-r-[14px] border-r-transparent border-b-[28px] border-b-rose-500 relative">
                <span className="absolute left-1/2 -translate-x-1/2 -top-3 text-xs">🎉</span>
              </div>
            </motion.div>
          )}

          {/* Lightbulb indicator for POINT / TUTORIAL state */}
          {state === 'point' && (
            <motion.div
              key="point-accessory"
              initial={{ scale: 0, opacity: 0, y: 10 }}
              animate={{ scale: [1, 1.2, 1], opacity: 1, y: [0, -3, 0] }}
              exit={{ scale: 0, opacity: 0, y: 5 }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="absolute -top-6 right-0 text-xl z-30"
            >
              💡
            </motion.div>
          )}

          {/* Listening Soundwaves for LISTEN state */}
          {state === 'listen' && (
            <motion.div
              key="listen-accessory"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ opacity: [0.4, 1, 0.4], scale: [0.9, 1.1, 0.9] }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ repeat: Infinity, duration: 1.2 }}
              className="absolute -top-5 right-1 text-lg z-30 font-black text-sky-500"
            >
              🎵
            </motion.div>
          )}

          {/* Golden Crown for WAVE / EXCELLENT state */}
          {state === 'wave' && (
            <motion.div
              key="wave-accessory"
              initial={{ scale: 0, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, rotate: [-5, 5, -5], y: 0 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="absolute -top-6 text-xl z-30"
            >
              👑
            </motion.div>
          )}
        </AnimatePresence>

        {/* Pure Visual Arham Character */}
        <ArhamCharacter state={state} />
      </motion.div>
    </div>
  );
};


